import { create } from '@bufbuild/protobuf'
import { EmptySchema } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  AutoBidOrderMethod,
  type AutoBidRule,
  AutoBidRuleSchema,
  IntradayBasis,
  IntradayBidConfigSchema,
  IntradayPriceMode,
  SpotBidConfigSchema,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import {
  type Decimal,
  DecimalSchema,
} from '@ebalance/proto/google/type/decimal_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import type { AutoBidSideSlot } from '~/pages/AdminApprovalFlow/models/autoBidSlot'

/**
 * 自動入札ルール 片側 (買=不足時補填 / 売=余剰時処分) の編集用ローカル状態。
 * limitPriceYenPerKwh は orderMethod === LIMIT の時のみ意味を持つ
 * (MARKET の時は未使用だが、 モーダルで指値に戻した時の初期値として保持する)。
 */
export type AutoBidSideLocal = {
  coveragePct: number
  orderMethod: AutoBidOrderMethod
  limitPriceYenPerKwh: number
}

/** spec §4 設定項目テーブル: デフォルト値 (カバー率100・発注方法指値・価格 買20.00/売5.00) */
export const AUTO_BID_DEFAULT_COVERAGE_PCT = 100
export const AUTO_BID_DEFAULT_LIMIT_PRICE: Record<'buy' | 'sell', number> = {
  buy: 20.0,
  sell: 5.0,
}

export function defaultAutoBidSide(side: 'buy' | 'sell'): AutoBidSideLocal {
  return {
    coveragePct: AUTO_BID_DEFAULT_COVERAGE_PCT,
    orderMethod: AutoBidOrderMethod.LIMIT,
    limitPriceYenPerKwh: AUTO_BID_DEFAULT_LIMIT_PRICE[side],
  }
}

/**
 * proto AutoBidRule (undefined = 未設定) → ローカル編集状態。未設定は
 * デフォルト値で開始する (AC-039/045)。 spot 以外の process_config
 * (intraday、または未設定) はスポット欄をデフォルト値で開始する (=
 * 時間前セクションが自身の intraday config を別途読む、AC-12)。
 */
export function autoBidSideFromRule(
  rule: AutoBidRule | undefined,
  side: 'buy' | 'sell',
): AutoBidSideLocal {
  if (rule === undefined || rule.processConfig.case !== 'spot') {
    return defaultAutoBidSide(side)
  }
  const spot = rule.processConfig.value
  return {
    coveragePct: rule.coveragePct,
    orderMethod: spot.orderMethod,
    limitPriceYenPerKwh:
      spot.limitPriceYenPerKwh !== undefined
        ? Number(spot.limitPriceYenPerKwh.value)
        : AUTO_BID_DEFAULT_LIMIT_PRICE[side],
  }
}

/**
 * 時間前入札の自動入札ルール 片側の編集用ローカル状態 (design AC-02〜AC-04)。
 * basis は priceMode === DETERMINE の時のみ、 fixedPriceYenPerKwh は
 * basis === FIXED の時のみ、 counterRefPriceYenPerKwh / bidPriceYenPerKwh は
 * priceMode === SNAP_UP の時のみ意味を持つ (モーダルで条件を切り替えた時の
 * 初期値として非表示中も保持する)。 spotReferenceArea は basis === SPOT_AREA_PRICE
 * の時のみ意味を持つ (AC-APF-042 / 039)。
 */
export type AutoBidIntradaySideLocal = {
  coveragePct: number
  priceMode: IntradayPriceMode
  basis: IntradayBasis
  fixedPriceYenPerKwh: number
  spotReferenceArea: AutoBidSpotReferenceArea
  adjustmentYenPerKwh: number
  counterRefPriceYenPerKwh: number
  bidPriceYenPerKwh: number
}

/**
 * 一括編集の参照エリア「対象エリアと同じ」 (AC-APF-039)。 保存時に backend が
 * エリアごとに各エリアへ解決するので、 frontend は展開しない。
 */
export const AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET = 'sameAsTarget'

/** 参照エリア: 沖縄を除く 9 エリアのどれか、 または一括の「対象エリアと同じ」。 */
export type AutoBidSpotReferenceArea =
  | AreaCode
  | typeof AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET

/** 参照エリアの初期値: エリア別は対象エリア、 一括 (対象エリアなし) は「対象エリアと同じ」。 */
function defaultSpotReferenceArea(
  targetArea: AreaCode | undefined,
): AutoBidSpotReferenceArea {
  return targetArea ?? AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET
}

/** spec §4「時間前入札特有」設定項目テーブルのデフォルト値。 */
export const AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT = 0
export const AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 20.0,
  sell: 5.0,
}
export const AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 15.0,
  sell: 8.0,
}
export const AUTO_BID_INTRADAY_DEFAULT_BID_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 15.0,
  sell: 8.0,
}

export function defaultAutoBidIntradaySide(
  side: 'buy' | 'sell',
  targetArea?: AreaCode,
): AutoBidIntradaySideLocal {
  return {
    coveragePct: AUTO_BID_DEFAULT_COVERAGE_PCT,
    priceMode: IntradayPriceMode.DETERMINE,
    basis: IntradayBasis.FIXED,
    fixedPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE[side],
    spotReferenceArea: defaultSpotReferenceArea(targetArea),
    adjustmentYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT,
    counterRefPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE[side],
    bidPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_BID_PRICE[side],
  }
}

/**
 * proto AutoBidRule (undefined = 未設定) → 時間前入札ローカル編集状態。
 * spot 以外の process_config (未設定含む) はデフォルト値で開始する。
 * targetArea はルールの対象エリア (一括編集は undefined) で、 参照エリアを
 * 持たないルールの参照エリアの初期値に使う。
 */
export function autoBidIntradaySideFromRule(
  rule: AutoBidRule | undefined,
  side: 'buy' | 'sell',
  targetArea?: AreaCode,
): AutoBidIntradaySideLocal {
  if (rule === undefined || rule.processConfig.case !== 'intraday') {
    return defaultAutoBidIntradaySide(side, targetArea)
  }
  const intraday = rule.processConfig.value
  return {
    coveragePct: rule.coveragePct,
    priceMode: intraday.priceMode,
    basis: intraday.basis ?? IntradayBasis.FIXED,
    fixedPriceYenPerKwh:
      intraday.fixedPriceYenPerKwh !== undefined
        ? Number(intraday.fixedPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE[side],
    spotReferenceArea:
      intraday.spotReference.case === 'referenceAreaCode'
        ? intraday.spotReference.value
        : defaultSpotReferenceArea(targetArea),
    adjustmentYenPerKwh:
      intraday.adjustmentYenPerKwh !== undefined
        ? Number(intraday.adjustmentYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT,
    counterRefPriceYenPerKwh:
      intraday.counterRefPriceYenPerKwh !== undefined
        ? Number(intraday.counterRefPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE[side],
    bidPriceYenPerKwh:
      intraday.bidPriceYenPerKwh !== undefined
        ? Number(intraday.bidPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_BID_PRICE[side],
  }
}

/**
 * 時間前入札 片側のモーダルの編集状態。 基準だけは未選択 (undefined) を取り得る
 * (参照エリアの無いスポット基準の側を開いた時、 AC-APF-076)。 保存できる形への
 * 変換は [[parseAutoBidIntradayDraft]]。
 */
export type AutoBidIntradaySideDraft = Omit<
  AutoBidIntradaySideLocal,
  'basis'
> & {
  basis: IntradayBasis | undefined
}

/**
 * 片側の slot → モーダルの初期値。 基準未選択の側は価格モード = 価格を決める・
 * 基準未選択で開き、 カバー率と調整は保存値、 その他の欄は既定値 (切り替えた時の
 * 初期値) にする。 基準 = 固定 に読み替えない (AC-APF-076 ※)。
 */
export function autoBidIntradayDraftFromSlot(
  slot: AutoBidSideSlot,
  side: 'buy' | 'sell',
  targetArea?: AreaCode,
): AutoBidIntradaySideDraft {
  switch (slot.kind) {
    case 'saved':
      return autoBidIntradaySideFromRule(slot.rule, side, targetArea)
    case 'basisUnselected':
      return {
        ...defaultAutoBidIntradaySide(side, targetArea),
        coveragePct: slot.side.coveragePct,
        priceMode: IntradayPriceMode.DETERMINE,
        basis: undefined,
        adjustmentYenPerKwh:
          slot.side.adjustmentYenPerKwh !== undefined
            ? Number(slot.side.adjustmentYenPerKwh.value)
            : AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT,
      }
    case 'neverSaved':
      return defaultAutoBidIntradaySide(side, targetArea)
  }
}

type AutoBidIntradaySubmissionCommon = {
  coveragePct: number
  adjustmentYenPerKwh: number
}

/**
 * 保存できる時間前入札 片側 (SaveAutoBidRule に送る形)。 価格モード × 基準ごとに
 * 送る欄だけを持ち、 基準未選択の「価格を決める」 は表せない。
 */
export type AutoBidIntradaySubmission =
  | (AutoBidIntradaySubmissionCommon & {
      priceMode: IntradayPriceMode.DETERMINE
      basis: IntradayBasis.FIXED
      fixedPriceYenPerKwh: number
    })
  | (AutoBidIntradaySubmissionCommon & {
      priceMode: IntradayPriceMode.DETERMINE
      basis: IntradayBasis.BEST_QUOTE | IntradayBasis.LATEST_TRADE
    })
  | (AutoBidIntradaySubmissionCommon & {
      priceMode: IntradayPriceMode.DETERMINE
      basis: IntradayBasis.SPOT_AREA_PRICE
      spotReferenceArea: AutoBidSpotReferenceArea
    })
  | (AutoBidIntradaySubmissionCommon & {
      priceMode: IntradayPriceMode.SNAP_UP
      counterRefPriceYenPerKwh: number
      bidPriceYenPerKwh: number
    })

/** 保存を拒否する理由。 文言 key は `AUTO_BID_INTRADAY_DRAFT_ERROR_MESSAGE_KEY`。 */
export type AutoBidIntradayDraftError = 'basisRequired'

export type AutoBidIntradayDraftParse =
  | { ok: true; submission: AutoBidIntradaySubmission }
  | { ok: false; error: AutoBidIntradayDraftError }

/**
 * 編集状態 → 保存できる形。 価格を決める で基準が未選択なら basisRequired
 * (AC-APF-076)。 食いつきは基準を送らないので検証しない。 範囲の検証は
 * [[validateAutoBidIntradaySide]] が持つ。
 */
export function parseAutoBidIntradayDraft(
  draft: AutoBidIntradaySideDraft,
): AutoBidIntradayDraftParse {
  const common = {
    coveragePct: draft.coveragePct,
    adjustmentYenPerKwh: draft.adjustmentYenPerKwh,
  }
  if (draft.priceMode === IntradayPriceMode.SNAP_UP) {
    return {
      ok: true,
      submission: {
        ...common,
        priceMode: IntradayPriceMode.SNAP_UP,
        counterRefPriceYenPerKwh: draft.counterRefPriceYenPerKwh,
        bidPriceYenPerKwh: draft.bidPriceYenPerKwh,
      },
    }
  }
  switch (draft.basis) {
    case IntradayBasis.FIXED:
      return {
        ok: true,
        submission: {
          ...common,
          priceMode: IntradayPriceMode.DETERMINE,
          basis: IntradayBasis.FIXED,
          fixedPriceYenPerKwh: draft.fixedPriceYenPerKwh,
        },
      }
    case IntradayBasis.BEST_QUOTE:
    case IntradayBasis.LATEST_TRADE:
      return {
        ok: true,
        submission: {
          ...common,
          priceMode: IntradayPriceMode.DETERMINE,
          basis: draft.basis,
        },
      }
    case IntradayBasis.SPOT_AREA_PRICE:
      return {
        ok: true,
        submission: {
          ...common,
          priceMode: IntradayPriceMode.DETERMINE,
          basis: IntradayBasis.SPOT_AREA_PRICE,
          spotReferenceArea: draft.spotReferenceArea,
        },
      }
    default:
      return { ok: false, error: 'basisRequired' }
  }
}

/**
 * 価格・調整の許容範囲 (spec §4 バリデーション表)。
 *
 * backend の実装 SoT と同値に保つこと:
 *   - go/internal/backend/domain/entity/autobid/spot_bid_config.go
 *     (minLimitPriceYenPerKwh / maxLimitPriceYenPerKwh)
 *   - go/internal/backend/domain/entity/autobid/intraday_bid_config.go
 *     (min/maxIntradayPriceYenPerKwh、 min/maxAdjustmentYenPerKwh)
 *
 * frontend 側の検証は UX 目的 (入力欄直下へインライン表示する: design/CLAUDE.md
 * 「入力エラーはインライン、 toast にしない」) であって業務制約の SoT ではない。
 */
export const AUTO_BID_MIN_PRICE = 0.01
export const AUTO_BID_MAX_PRICE = 999
export const AUTO_BID_MIN_ADJUSTMENT = -999
export const AUTO_BID_MAX_ADJUSTMENT = 999

/**
 * 範囲エラーの種別。 models は文言を持たず、 文言 key への写像は
 * `constants/autoBidValidationMessageKey.ts` の `AUTO_BID_VALIDATION_MESSAGE_KEY` が持つ。
 */
export type AutoBidValidationError = 'price' | 'adjustment'

/**
 * 価格 (指値 / 固定価格 / 対向参照価格 / 入札価格) の範囲検証。
 *
 * step 0.01 は検証しない: `decimal()` が送信時に `toFixed(2)` で丸めるため
 * 刻み違反が backend へ到達しない。
 */
export function validateAutoBidPrice(
  v: number,
): AutoBidValidationError | undefined {
  return Number.isNaN(v) || v < AUTO_BID_MIN_PRICE || v > AUTO_BID_MAX_PRICE
    ? 'price'
    : undefined
}

/** 基準からの調整 の範囲検証。 step 0.01 を検証しない理由は上と同じ。 */
export function validateAutoBidAdjustment(
  v: number,
): AutoBidValidationError | undefined {
  return Number.isNaN(v) ||
    v < AUTO_BID_MIN_ADJUSTMENT ||
    v > AUTO_BID_MAX_ADJUSTMENT
    ? 'adjustment'
    : undefined
}

/**
 * スポット入札 片側の範囲検証。 指値価格は orderMethod === LIMIT の時のみ
 * 送信される (`autoBidSpotSideToProto`) ため、 成行では検証しない。
 */
export function validateAutoBidSide(
  side: AutoBidSideLocal,
): AutoBidValidationError | undefined {
  return side.orderMethod === AutoBidOrderMethod.LIMIT
    ? validateAutoBidPrice(side.limitPriceYenPerKwh)
    : undefined
}

/** 時間前入札 片側の項目別エラー (undefined = 当該項目はエラーなし)。 */
export type AutoBidIntradayFieldErrors = {
  fixedPrice: AutoBidValidationError | undefined
  counterRefPrice: AutoBidValidationError | undefined
  bidPrice: AutoBidValidationError | undefined
  adjustment: AutoBidValidationError | undefined
}

/**
 * 時間前入札 片側の範囲検証。 priceMode / basis により送信される項目が
 * 切り替わる (`autoBidIntradaySideToProto`) ため、 送信されない項目
 * (= モーダル上でも非表示) は検証しない。 調整は常時送信される。
 */
export function validateAutoBidIntradaySide(
  side: AutoBidIntradaySideDraft,
): AutoBidIntradayFieldErrors {
  const isDetermine = side.priceMode === IntradayPriceMode.DETERMINE
  const isFixed = side.basis === IntradayBasis.FIXED
  return {
    fixedPrice:
      isDetermine && isFixed
        ? validateAutoBidPrice(side.fixedPriceYenPerKwh)
        : undefined,
    counterRefPrice: !isDetermine
      ? validateAutoBidPrice(side.counterRefPriceYenPerKwh)
      : undefined,
    bidPrice: !isDetermine
      ? validateAutoBidPrice(side.bidPriceYenPerKwh)
      : undefined,
    adjustment: validateAutoBidAdjustment(side.adjustmentYenPerKwh),
  }
}

/**
 * 時間前入札 片側の最初の範囲エラー (undefined = エラーなし)。 保存ボタンの
 * 無効化と、 無効理由の tooltip 文言に使う。
 */
export function firstAutoBidIntradayError(
  side: AutoBidIntradaySideDraft,
): AutoBidValidationError | undefined {
  return Object.values(validateAutoBidIntradaySide(side)).find(
    (e) => e !== undefined,
  )
}

/** 円/kWh の表示整形 (小数第2位固定)。 */
function formatPrice(v: number): string {
  return v.toFixed(2)
}

/**
 * 時間前入札の要約で使う価格整形。 **末尾の 0 を落とす** (`20.00` ではなく `20`)。
 *
 * spot 側の [[formatPrice]] (`toFixed(2)`) と違うのは、 AC-APF-047 の書式が
 * prototype の出荷済み実装 (`js/autobid-rule.js` の `fmtNum`:
 * `(Math.round(v * 100) / 100).toString()`) から起こされているため。 spec の例も
 * `固定20` / `+0.5` と末尾 0 が無い形で書かれている。 2 桁固定にすると
 * spec・prototype の両方と表示が食い違う。
 */
function formatIntradayNumber(v: number): string {
  return (Math.round(v * 100) / 100).toString()
}

/**
 * 「基準からの調整」の後置表示。 **0 は空文字** (AC-APF-047:「0 の時は表示せず、
 * 0 以外は符号付きで後置する」)。 prototype の `fmtSigned` と同値。
 */
function formatIntradayAdjustment(v: number): string {
  if (v === 0 || Number.isNaN(v)) return ''
  return `${v > 0 ? '+' : ''}${formatIntradayNumber(v)}`
}

/**
 * 基準「最良気配」の表示名。 買いは最良売り気配・売りは最良買い気配を参照するため、
 * 英語は売買で訳し分ける (approval-flow.md 付録「売買を区別しない訳は設けない」)。
 * 日本語はどちらも「最良気配」。
 */
export function intradayBestQuoteLabel(
  t: TFunction,
  sideKind: 'buy' | 'sell',
): string {
  return sideKind === 'buy'
    ? t('page:adminApprovalFlow.autoBid.modal.intraday.basisBestQuoteBuy')
    : t('page:adminApprovalFlow.autoBid.modal.intraday.basisBestQuoteSell')
}

/**
 * 基準 (最良気配 / 直近約定 / スポット / 固定) の要約での表示名。 modal と同じ
 * i18n キーを使う。 スポットは参照エリアが対象エリアと違う時だけ
 * `スポット ({参照エリア})` にする (AC-APF-047。 かっこは conventions.md「かっこ」に従い半角)。 「対象エリアと同じ」は
 * 各エリアで対象エリア自身なので `スポット` のまま。
 */
function intradayBasisLabel(
  t: TFunction,
  side: AutoBidIntradaySideLocal,
  sideKind: 'buy' | 'sell',
  targetArea: AreaCode | undefined,
): string {
  switch (side.basis) {
    case IntradayBasis.BEST_QUOTE:
      return intradayBestQuoteLabel(t, sideKind)
    case IntradayBasis.LATEST_TRADE:
      return t('page:adminApprovalFlow.autoBid.modal.intraday.basisLatestTrade')
    case IntradayBasis.SPOT_AREA_PRICE:
      return side.spotReferenceArea ===
        AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET ||
        side.spotReferenceArea === targetArea
        ? t('page:adminApprovalFlow.autoBid.modal.intraday.basisSpotAreaPrice')
        : t('page:adminApprovalFlow.autoBid.detailIntradaySpotReferenceArea', {
            area: convertAreaLabel(side.spotReferenceArea),
          })
    default:
      return t('page:adminApprovalFlow.autoBid.modal.intraday.basisFixed')
  }
}

/**
 * 時間前入札のセル / 一括要約テキスト (AC-APF-047 の `{発注内容}`)。
 *
 * | 条件 | 書式 |
 * |---|---|
 * | 価格を決める・基準 = 固定 | `固定{固定価格}{調整}` |
 * | 価格を決める・基準 ≠ 固定 | `{基準}{調整}` |
 * | 価格を決める・基準 = スポット・参照エリア ≠ 対象エリア | `スポット ({参照エリア}){調整}` |
 * | 食いつき | `食いつき 対向{≤\|≥}{対向参照価格}→入札{入札価格}{調整}` |
 *
 * targetArea はセルの対象エリア (一括 = 自動 の要約では代表エリア)。
 *
 * 比較記号は **買い = `≤` / 売り = `≥`** (対向 = 買い側から見た売り気配 /
 * 売り側から見た買い気配)。
 *
 * **spot 形 (`指値` / `成行`) で描いてはならない** — 価格モード・基準・対向参照価格は
 * 時間前にしか無い項目で、 spot 形に落とすと保存済みのルールが既定値に化けて見える
 * (AC-APF-047 の注記、 鉄則2 / AC-APF-056a)。
 */
export function autoBidIntradaySideSummaryText(
  t: TFunction,
  side: AutoBidIntradaySideLocal,
  sideKind: 'buy' | 'sell',
  fee: AutoBidFeeAutoConsider,
  targetArea?: AreaCode,
): string {
  const adjustment = formatIntradayAdjustment(side.adjustmentYenPerKwh)
  // 手数料自動考慮 ON の保存値は希望価格なので `→入札` を `→希望` にする (AC-APF-047)。
  const snapUpKey = isAutoBidFeeAutoConsiderOn(fee)
    ? 'page:adminApprovalFlow.autoBid.detailIntradaySnapUpDesired'
    : 'page:adminApprovalFlow.autoBid.detailIntradaySnapUp'
  const detail =
    side.priceMode === IntradayPriceMode.SNAP_UP
      ? t(snapUpKey, {
          comparator: sideKind === 'buy' ? '≤' : '≥',
          counterRef: formatIntradayNumber(side.counterRefPriceYenPerKwh),
          bid: formatIntradayNumber(side.bidPriceYenPerKwh),
          adjustment,
        })
      : side.basis === IntradayBasis.FIXED
        ? t('page:adminApprovalFlow.autoBid.detailIntradayFixed', {
            price: formatIntradayNumber(side.fixedPriceYenPerKwh),
            adjustment,
          })
        : t('page:adminApprovalFlow.autoBid.detailIntradayBasis', {
            basis: intradayBasisLabel(t, side, sideKind, targetArea),
            adjustment,
          })
  return t('page:adminApprovalFlow.autoBid.summaryFormat', {
    coverage: side.coveragePct,
    detail,
  })
}

/** セル要約テキスト (AC-01 書式: `カバー率{N}% / {発注内容}`)。 */
export function autoBidSideSummaryText(
  t: TFunction,
  side: AutoBidSideLocal,
): string {
  const detail =
    side.orderMethod === AutoBidOrderMethod.MARKET
      ? t('page:adminApprovalFlow.autoBid.detailMarket')
      : t('page:adminApprovalFlow.autoBid.detailLimit', {
          price: formatPrice(side.limitPriceYenPerKwh),
        })
  return t('page:adminApprovalFlow.autoBid.summaryFormat', {
    coverage: side.coveragePct,
    detail,
  })
}

/** bid-limits の価格レンジ (undefined = 制限なし)。 */
export type AutoBidPriceRange = {
  min: number | undefined
  max: number | undefined
}

export type AutoBidEffective = {
  /** 発注に使われる想定の入札価格。 成行かつ成行許可の場合は undefined (指値変換なし)。 */
  effectivePrice: number | undefined
  /** 表示に⚠を付けるべきか (= 上下限で clamp、 JEPX 値域外、 または成行不可 → 指値変換)。 */
  warning: boolean
  /** 成行不可のため指値に変換されたか (AC-043/047)。 */
  marketConverted: boolean
  /** 入札価格が JEPX の値域外で、 発火時に発注されないか (AC-APF-043 / 053)。 */
  outsideJepx: boolean
}

/**
 * 価格の実効値 (上下限を適用した後の入札価格、 AC-APF-043)。
 * `outsideJepx` は clamp 後も JEPX の値域を外れ、 発火時にスキップされる価格。
 */
export type AutoBidPriceEffective =
  | { kind: 'price'; value: number; clamped: boolean }
  | { kind: 'outsideJepx'; value: number }

/** JEPX 制度の入札価格の値域 (円/kWh、 AC-APF-043 / 053)。 */
const JEPX_MIN_PRICE_SEN = 1
const JEPX_MAX_PRICE_SEN = 99900

/**
 * 円/kWh を 0.01 円単位の整数へ丸める。 backend は decimal で計算するため、
 * 浮動小数の加算誤差 (998.99 + 0.01 等) で境界の判定がずれないよう整数で比べる。
 */
function toSen(yen: number): number {
  return Math.round(yen * 100)
}

/**
 * desired (手数料と上下限を適用する前の価格) から価格の実効値を求める。
 * 上下限で clamp した後に JEPX の値域を判定する (spec §3「手数料自動考慮との関係」)。
 * 表示専用で、 発火時の値は backend `IntradayBidConfig.DesiredPrice` と
 * 実行層の clamp が決める。
 */
export function computeAutoBidPriceEffective(
  desiredYenPerKwh: number,
  range: AutoBidPriceRange,
): AutoBidPriceEffective {
  const desired = toSen(desiredYenPerKwh)
  const min = range.min !== undefined ? toSen(range.min) : undefined
  const max = range.max !== undefined ? toSen(range.max) : undefined
  let sen = desired
  if (min !== undefined && sen < min) sen = min
  if (max !== undefined && sen > max) sen = max
  const value = sen / 100
  if (sen < JEPX_MIN_PRICE_SEN || sen > JEPX_MAX_PRICE_SEN) {
    return { kind: 'outsideJepx', value }
  }
  return { kind: 'price', value, clamped: sen !== desired }
}

/**
 * 手数料自動考慮 (`bid-limits.md` AC-BDL-015〜020) の、 ルールの価格の扱い。
 * `unavailable` は ON だが手数料単価を使えない間 (取得中・取得失敗・使えない単価)。
 * 欄名は ON のまま、 価格の実効値は出さない (発火は代替の単価で加減算するため、
 * 未調整の値を見せると入札価格とずれる。 AC-APF-068)。
 */
export type AutoBidFeeAutoConsider =
  | { kind: 'off' }
  | { kind: 'on'; feeYenPerKwh: number }
  | { kind: 'unavailable' }

export const AUTO_BID_FEE_OFF: AutoBidFeeAutoConsider = { kind: 'off' }

/** 欄名・サマリを希望価格の呼び方にするか (AC-APF-041 / 042 / 047)。 */
export function isAutoBidFeeAutoConsiderOn(
  fee: AutoBidFeeAutoConsider,
): boolean {
  return fee.kind !== 'off'
}

/**
 * 希望価格に手数料単価を加減算した入札価格 (売り + / 買い −。 backend
 * `ComputeFeeAdjustedOrderPrice` と同じ符号)。 0.01 円単位の整数で足す。
 *
 * rule (業務計算は backend) から外れて frontend に置く: 上下限の clamp と同じ表示専用の
 * プレビューで、 入札価格は backend の発火が決める。 入札画面の preview RPC は「範囲外は
 * 作成不可」 で、 本画面の「範囲外は clamp して ⚠」 と意味が違うので使わない。 単価の
 * 読み元が組織ごと・DB 由来になった時か、 この値が保存の可否を決めるようになった時は
 * backend に寄せる (#11191)。
 */
export function autoBidFeeAdjustedPrice(
  desiredYenPerKwh: number,
  sideKind: 'buy' | 'sell',
  feeYenPerKwh: number,
): number {
  const fee = toSen(feeYenPerKwh)
  return (toSen(desiredYenPerKwh) + (sideKind === 'sell' ? fee : -fee)) / 100
}

/**
 * 手数料の加減算 → 上下限の clamp → JEPX 値域の判定 (spec §3「手数料自動考慮との
 * 関係」)。 clamp の判定は加減算後の値と比べるので、 希望価格が範囲内でも加減算後に
 * 範囲外なら clamped になる (AC-APF-043)。 単価を使えない間は undefined。
 */
function computeAutoBidFeeAwarePriceEffective(
  desiredYenPerKwh: number,
  sideKind: 'buy' | 'sell',
  range: AutoBidPriceRange,
  fee: AutoBidFeeAutoConsider,
): AutoBidPriceEffective | undefined {
  switch (fee.kind) {
    case 'off':
      return computeAutoBidPriceEffective(desiredYenPerKwh, range)
    case 'on':
      return computeAutoBidPriceEffective(
        autoBidFeeAdjustedPrice(desiredYenPerKwh, sideKind, fee.feeYenPerKwh),
        range,
      )
    case 'unavailable':
      return undefined
  }
}

/**
 * 時間前ルールの desired (価格を直接入力する欄 + 基準からの調整)。
 * 基準 = 固定なら固定価格 + 調整、 食いつきなら入札価格 + 調整 (backend
 * `IntradayBidConfig.DesiredPrice` と同じ)。 動的基準 (最良気配 / 直近約定) は
 * 設定時に価格が定まらないので undefined (AC-APF-043)。 入力途中で数値に
 * ならない時も undefined を返す。 各欄は保存時 ([[decimal]]) と同じく 0.01 単位に
 * 丸めてから足す (発火時の価格は保存値から決まるため)。
 */
export function autoBidIntradayDesiredPrice(
  side: AutoBidIntradaySideDraft,
): number | undefined {
  let base: number
  if (side.priceMode === IntradayPriceMode.SNAP_UP) {
    base = side.bidPriceYenPerKwh
  } else if (side.basis === IntradayBasis.FIXED) {
    base = side.fixedPriceYenPerKwh
  } else {
    return undefined
  }
  const desired = asSaved(base) + asSaved(side.adjustmentYenPerKwh)
  return Number.isNaN(desired) ? undefined : desired
}

function effectiveFromPrice(price: AutoBidPriceEffective): AutoBidEffective {
  return price.kind === 'outsideJepx'
    ? {
        effectivePrice: price.value,
        warning: true,
        marketConverted: false,
        outsideJepx: true,
      }
    : {
        effectivePrice: price.value,
        warning: price.clamped,
        marketConverted: false,
        outsideJepx: false,
      }
}

/**
 * ルールデータ自体は書き換えず (spec AC-043/047)、 bid-limits の実効値を
 * 計算する表示専用ロジック (サーバ確定値の再計算ではない UX プレビュー。
 * 実際の発注時 clamp は
 * 実行層 usecase の責務、#2511)。
 */
export function computeAutoBidEffective(
  side: AutoBidSideLocal,
  sideKind: 'buy' | 'sell',
  priceRange: AutoBidPriceRange,
  marketOrderAllowed: boolean,
  fee: AutoBidFeeAutoConsider,
): AutoBidEffective | undefined {
  if (side.orderMethod === AutoBidOrderMethod.MARKET) {
    if (marketOrderAllowed) {
      return {
        effectivePrice: undefined,
        warning: false,
        marketConverted: false,
        outsideJepx: false,
      }
    }
    // 成行不可 → 買=実効上限 / 売=実効下限 で指値変換 (spec §4 発注方法備考)。
    // 上下限が空欄 (制限なし) の側は JEPX の値域端になる (backend
    // `bidlimit.Matrix.EffectivePriceRange` と揃える)。
    const converted =
      sideKind === 'buy'
        ? (priceRange.max ?? AUTO_BID_MAX_PRICE)
        : (priceRange.min ?? AUTO_BID_MIN_PRICE)
    return {
      effectivePrice: converted,
      warning: true,
      marketConverted: true,
      outsideJepx: false,
    }
  }
  const price = computeAutoBidFeeAwarePriceEffective(
    asSaved(side.limitPriceYenPerKwh),
    sideKind,
    priceRange,
    fee,
  )
  return price === undefined ? undefined : effectiveFromPrice(price)
}

/**
 * 時間前ルール片側の実効値。 価格の実効値を出せない (動的基準) 時は undefined で、
 * ⚠ の対象にもしない (AC-APF-047 の注記)。
 */
export function computeAutoBidIntradayEffective(
  side: AutoBidIntradaySideDraft,
  sideKind: 'buy' | 'sell',
  priceRange: AutoBidPriceRange,
  fee: AutoBidFeeAutoConsider,
): AutoBidEffective | undefined {
  const desired = autoBidIntradayDesiredPrice(side)
  if (desired === undefined) return undefined
  const price = computeAutoBidFeeAwarePriceEffective(
    desired,
    sideKind,
    priceRange,
    fee,
  )
  return price === undefined ? undefined : effectiveFromPrice(price)
}

/** 保存時に送る値 ([[decimal]] の丸め) を number で返す。 */
function asSaved(v: number): number {
  return Number(v.toFixed(2))
}

function decimal(v: number): Decimal {
  return create(DecimalSchema, { value: v.toFixed(2) })
}

export function autoBidSpotSideToProto(
  area: AreaCode,
  bidSide: Side,
  side: AutoBidSideLocal,
): AutoBidRule {
  return create(AutoBidRuleSchema, {
    areaCode: area,
    bidSide,
    coveragePct: side.coveragePct,
    processConfig: {
      case: 'spot',
      value: create(SpotBidConfigSchema, {
        orderMethod: side.orderMethod,
        limitPriceYenPerKwh:
          side.orderMethod === AutoBidOrderMethod.LIMIT
            ? decimal(side.limitPriceYenPerKwh)
            : undefined,
      }),
    },
  })
}

export function autoBidIntradaySideToProto(
  area: AreaCode,
  bidSide: Side,
  side: AutoBidIntradaySubmission,
): AutoBidRule {
  const isSpot =
    side.priceMode === IntradayPriceMode.DETERMINE &&
    side.basis === IntradayBasis.SPOT_AREA_PRICE
  return create(AutoBidRuleSchema, {
    areaCode: area,
    bidSide,
    coveragePct: side.coveragePct,
    processConfig: {
      case: 'intraday',
      value: create(IntradayBidConfigSchema, {
        priceMode: side.priceMode,
        basis:
          side.priceMode === IntradayPriceMode.DETERMINE
            ? side.basis
            : undefined,
        fixedPriceYenPerKwh:
          side.priceMode === IntradayPriceMode.DETERMINE &&
          side.basis === IntradayBasis.FIXED
            ? decimal(side.fixedPriceYenPerKwh)
            : undefined,
        spotReference: !isSpot
          ? { case: undefined }
          : side.spotReferenceArea === AUTO_BID_SPOT_REFERENCE_SAME_AS_TARGET
            ? { case: 'sameAsTargetArea', value: create(EmptySchema) }
            : { case: 'referenceAreaCode', value: side.spotReferenceArea },
        adjustmentYenPerKwh: decimal(side.adjustmentYenPerKwh),
        counterRefPriceYenPerKwh:
          side.priceMode === IntradayPriceMode.SNAP_UP
            ? decimal(side.counterRefPriceYenPerKwh)
            : undefined,
        bidPriceYenPerKwh:
          side.priceMode === IntradayPriceMode.SNAP_UP
            ? decimal(side.bidPriceYenPerKwh)
            : undefined,
      }),
    },
  })
}
